/*
 * One edit of a text field: the whole next value and where the selection ends up. Every helper in
 * this directory is a pure function from (text, selection) to an `Edit`, and `applyEdit` is the
 * one place that puts an edit into a real textarea — through `execCommand("insertText")` where
 * the engine has it, so the browser's own undo stack keeps every shortcut and paste as one step
 * (spec 12 §4.4, A28), with a plain rewrite as the fallback.
 */

export interface Edit {
	text: string
	start: number
	end: number
}

export function applyEdit(textarea: HTMLTextAreaElement, edit: Edit): void {
	const { value } = textarea
	// The changed range: strip the common prefix and suffix so the engine replaces as little as
	// possible and the undo entry reads as what the user did.
	let from = 0
	while (from < value.length && from < edit.text.length && value[from] === edit.text[from]) from++
	let oldTo = value.length
	let newTo = edit.text.length
	while (oldTo > from && newTo > from && value[oldTo - 1] === edit.text[newTo - 1]) {
		oldTo--
		newTo--
	}
	const insert = edit.text.slice(from, newTo)

	textarea.focus()
	textarea.setSelectionRange(from, oldTo)
	let done = false
	if (typeof document.execCommand === "function") {
		try {
			done =
				insert === ""
					? document.execCommand("delete")
					: document.execCommand("insertText", false, insert)
		} catch {
			done = false
		}
	}
	if (!done || textarea.value !== edit.text) {
		textarea.setRangeText(insert, from, oldTo, "end")
		textarea.dispatchEvent(new Event("input", { bubbles: true }))
	}
	textarea.setSelectionRange(edit.start, edit.end)
}
